import Typography from '@ixigo/iui/Typography';
import { ChevronLeft, Search } from '@ixigo/icons';
import { ChangeEvent, FC, useEffect, useMemo, useState } from 'react';
import * as FlagIcons from 'country-flag-icons/react/1x1';
import type { CountryCode } from 'libphonenumber-js';
import Dialog from '../Dialog';
import type { CountryCodeSelectorProps, CountryOption } from './index.types';

type FlagComponent = (props: React.SVGProps<SVGSVGElement>) => JSX.Element;
const FLAG_MAP = FlagIcons as Record<string, FlagComponent>;

const getCountryFlagKey = (code: string): string => code.replace(/-/g, '_').toUpperCase();

const getCountryFlagEmoji = (code: string): string => {
    if (!code || code.length < 2) return '';
    const c = code.toUpperCase().replace(/-/g, '_');
    if (c.length === 2) {
        const [a, b] = c;
        return String.fromCodePoint(0x1f1e6 - 65 + a.charCodeAt(0), 0x1f1e6 - 65 + b.charCodeAt(0));
    }
    return '';
};

const CountryFlag = ({ code, className = '' }: { code: string; className?: string }) => {
    const key = getCountryFlagKey(code);
    const FlagSvg = FLAG_MAP[key];
    if (!FlagSvg) {
        return (
            <span className={`inline-flex items-center justify-center text-lg leading-none ${className}`} aria-hidden>
                {getCountryFlagEmoji(code)}
            </span>
        );
    }
    return (
        <span
            className={`inline-flex shrink-0 overflow-hidden rounded-full bg-charcoal-100 ${className}`}
            aria-hidden
            title={code}
        >
            <FlagSvg className='h-full w-full object-cover' />
        </span>
    );
};

const filterCountriesByQuery = (countries: CountryOption[], query: string): CountryOption[] => {
    if (!query.trim()) return countries;
    const q = query.trim().toLowerCase();
    return countries.filter(
        (c) => c.name.toLowerCase().includes(q) || c.code.toLowerCase().includes(q) || c.dialCode.includes(q),
    );
};

interface CountryCodeOptionProps {
    country: CountryOption;
    onSelect: (code: CountryCode) => void;
}

const CountryCodeOption: FC<CountryCodeOptionProps> = ({ country, onSelect }) => (
    <li
        className='flex justify-between items-center gap-10 px-20 py-15 border-charcoal hover:border-brand hover:bg-seville-50 cursor-pointer'
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => onSelect(country.code)}
    >
        <div className='flex items-center gap-10 min-w-0'>
            <CountryFlag code={country.code} className='w-6 h-6' />
            <Typography>{country.name}</Typography>
        </div>
        <Typography className='text-charcoal-600 shrink-0'>{country.dialCode}</Typography>
    </li>
);

const CountryCodeSelector: FC<CountryCodeSelectorProps> = ({
    open,
    onClose,
    onSelect,
    locationBasedCountries,
    alphabeticalCountries,
}) => {
    const [searchQuery, setSearchQuery] = useState('');

    useEffect(() => {
        if (!open) setSearchQuery('');
    }, [open]);

    const filteredLocationBased = useMemo(
        () => filterCountriesByQuery(locationBasedCountries, searchQuery),
        [locationBasedCountries, searchQuery],
    );
    const filteredAlphabetical = useMemo(
        () => filterCountriesByQuery(alphabeticalCountries, searchQuery),
        [alphabeticalCountries, searchQuery],
    );

    const handleSelect = (code: CountryCode) => {
        onSelect(code);
        onClose();
    };

    const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(e.target.value);
    };

    return (
        <Dialog open={open} onClose={onClose} classes={{ container: '!min-w-full !min-h-full !rounded-none' }}>
            <div className='px-20 py-10 flex items-center sticky top-0 z-10 w-full bg-primary'>
                <button
                    type='button'
                    onClick={onClose}
                    className='shrink-0 p-0 border-none bg-transparent cursor-pointer outline-none focus:outline-none'
                    aria-label='Back'
                >
                    <ChevronLeft className='text-[1.5rem]' />
                </button>
                <div className='inline-flex flex-col text-secondary w-full py-5 rounded-[40px] ml-[18px]'>
                    <div className='flex relative group/input px-15 rounded-10 items-center'>
                        <div className='absolute inset-0 transition-[border] select-none pointer-events-none border group-hover/input:border-2 border-primary rounded-[40px]' />
                        <div className='flex flex-grow items-center'>
                            <input
                                type='search'
                                placeholder='Search Country'
                                value={searchQuery}
                                onChange={handleSearchChange}
                                className='outline-none w-full bg-transparent placeholder:text-disabled py-3 focus:caret-selection text-primary text-sm'
                            />
                        </div>
                        <Search className='text-[1.5rem] shrink-0' />
                    </div>
                </div>
            </div>
            <div className='overflow-y-auto'>
                {filteredLocationBased.length > 0 && (
                    <>
                        <Typography variant='body-sm' className='px-20 pt-15 pb-5 text-charcoal-600 font-medium'>
                            Based on your location
                        </Typography>
                        <ul>
                            {filteredLocationBased.map((country) => (
                                <CountryCodeOption key={country.code} country={country} onSelect={handleSelect} />
                            ))}
                        </ul>
                    </>
                )}
                {filteredAlphabetical.length > 0 && (
                    <>
                        <Typography variant='body-sm' className='px-20 pt-15 pb-5 text-charcoal-600 font-medium'>
                            Alphabetical
                        </Typography>
                        <ul>
                            {filteredAlphabetical.map((country) => (
                                <CountryCodeOption key={country.code} country={country} onSelect={handleSelect} />
                            ))}
                        </ul>
                    </>
                )}
                {searchQuery.trim() && filteredLocationBased.length === 0 && filteredAlphabetical.length === 0 && (
                    <Typography variant='body-sm' className='px-20 py-20 text-charcoal-600 text-center'>
                        No country found for your search.
                    </Typography>
                )}
            </div>
        </Dialog>
    );
};

export default CountryCodeSelector;
